import Vue from "vue";
import Router from "vue-router";
import Home from "@/views/Home.vue";
import store from "@/domain/store";
import * as util from "@/util";
import { PartialPermission } from "@/domain";
import config from "@/config";
import gtag from "./gtag";
import { Dictionary, Route } from "vue-router/types/router";
import { objEquals } from "@/util";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/home",
      name: "home",
      component: Home,
      meta: { userLogged: false, userAdmin: false, avoidRedirect: true }
    },
    {
      path: "/error",
      name: "error",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/Error.vue"),
      meta: { userLogged: false, userAdmin: false }
    },
    {
      path: "/contact",
      name: "contact",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/Contact.vue"),
      meta: { userLogged: false, userAdmin: false, avoidRedirect: true }
    },
    {
      path: "/terminalQrConfig",
      name: "terminalQrConfig",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/TerminalQRConfig.vue"),
      meta: { userLogged: false, userAdmin: false, avoidRedirect: true }
    },
    {
      path: "/authregister",
      name: "authregister",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/AuthRegister.vue"),
      meta: { userLogged: false, userAdmin: false }
    },
    {
      alias: "/",
      path: "/authlogin",
      name: "authlogin",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/AuthLogin.vue"),
      meta: { userLogged: false, userAdmin: false }
    },
    {
      path: "/authreinforce",
      name: "authreinforce",
      component: () => import(/* webpackChunkName: "mainviews" */ "@/views/AuthReinforce.vue"),
      meta: { userLogged: false, userAdmin: false }
    },
    {
      path: "/companyCatalog",
      name: "companyCatalog",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/CompanyCatalog.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/companyInfo",
      name: "companyInfo",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/CompanyInfo.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/connectionstatus",
      name: "connectionstatus",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/ConnectionStatus.vue"),
      meta: { userLogged: true, userAdmin: false, allowConnectionGraph: true }
    },
    {
      path: "/transactionquery",
      name: "transactionquery",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/TransactionQueryV3.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/usermanager",
      name: "usermanager",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/UserManager.vue"),
      meta: { userLogged: true, userAdmin: true, multiAdmin: true }
    },
    {
      path: "/usersettings",
      name: "usersettings",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/UserSettings.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/management",
      name: "management",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/Management.vue"),
      meta: { userLogged: true, tokenAdmin: true }
    },
    {
      path: "/branchConfig",
      name: "branchConfig",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/BranchConfig.vue"),
      meta: { userLogged: true, userAdmin: true, multiAdmin: true }
    },
    {
      path: "/vendingConfig",
      name: "vendingConfig",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/VendingConfig.vue"),
      meta: { userLogged: true }
    },
    {
      path: "/topayMessages",
      name: "topayMessages",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/TopayMessages.vue"),
      meta: { userLogged: true, topayAdmin: true }
    },
    {
      path: "/euroshellcertificate",
      name: "euroshellcertificate",
      component: () => import(/* webpackChunkName: "adminviews" */ "@/views/EuroshellCertificate.vue"),
      meta: { userLogged: true, euroshellCertificate: true }
    },
    {
      path: "/paymentEdit",
      name: "paymentEdit",
      component: () => import(/* webpackChunkName: "paymentlink" */ "@/views/PaymentEdit.vue"),
      meta: { userLogged: true, paymentLink: true }
    },
    {
      path: "/paymentList",
      name: "paymentList",
      component: () => import(/* webpackChunkName: "paymentlink" */ "@/views/PaymentList.vue"),
      meta: { userLogged: true, paymentLink: true }
    },
    {
      path: "/changelog",
      name: "changelog",
      component: () => import(/* webpackChunkName: "userviews" */ "@/views/Changelog.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/paymentLinkAPI",
      name: "paymentLinkAPI",
      component: () => import(/* webpackChunkName: "clientapi" */ "@/views/PaymentLinkAPI.vue"),
      meta: { userLogged: true, userAdmin: false }
    },
    {
      path: "/clientAPI",
      name: "clientAPI",
      component: () => import(/* webpackChunkName: "clientapi" */ "@/views/ClientAPI.vue"),
      meta: { userLogged: false, userAdmin: false, avoidRedirect: true }
    }
  ]
});

interface SimpleRoute {
  fullPath: string;
  params: Dictionary<string>;
}
type AnyRoute = string | Route | SimpleRoute | null;
export function simplifyRoute(route: AnyRoute): SimpleRoute | null {
  if (!route) return null;
  const result: SimpleRoute = {
    fullPath: "",
    params: {}
  };
  if (typeof route === "string") {
    result.fullPath = route;
  } else {
    const vueRoute = route as Partial<Route>;
    result.fullPath = vueRoute.fullPath || vueRoute.path || "";
    result.params = route.params || {};
  }
  if (!result.fullPath) return null;
  return result;
}

export function routeEquals(route1: AnyRoute, route2: AnyRoute): boolean {
  const r1 = simplifyRoute(route1);
  const r2 = simplifyRoute(route2);
  if (!r1 || !r2) return !r1 && !r2; // Check for nulls
  return r1.fullPath === r2.fullPath && objEquals(r1.params, r2.params);
}

export function getUserStartPage(): string {
  if (!store.state.currentUser) return "/";
  const permission: PartialPermission = store.getters.userPermission;
  if (store.state.selectedCompany) {
    if (util.isNull(permission.txnView) || permission.txnView) return "/transactionquery";
    if (permission.vendingAdmin) return "/vendingConfig";
    if (permission.allowConnectionGraph) return "/companyInfo";
    if (permission.paymentLink) return "/paymentList";
    if (permission.isAdminOfAny) return "/usermanager";
  }
  return "/companyCatalog";
}

interface RoutePermissionCheck {
  metaKey: string;
  permKey: keyof PartialPermission;
  errorMessage: string;
}

const ROUTE_PERMISSION_CHECKS: RoutePermissionCheck[] = [
  {
    metaKey: "allowConnectionGraph",
    permKey: "allowConnectionGraph",
    errorMessage: "User can't view connections graph"
  },
  { metaKey: "paymentLink", permKey: "paymentLink", errorMessage: "User can't view payment link" },
  {
    metaKey: "euroshellCertificate",
    permKey: "euroshellCertificate",
    errorMessage: "User can't view euroshell certificate"
  },
  { metaKey: "topayAdmin", permKey: "topayAdmin", errorMessage: "User can't view topay messages" }
];

function checkRoutePermissions(to: Route, permission: PartialPermission): string | null {
  for (const check of ROUTE_PERMISSION_CHECKS) {
    if (to.meta?.[check.metaKey] && !permission[check.permKey]) {
      return check.errorMessage;
    }
  }
  return null;
}

function checkAdminMeta(to: Route, permission: PartialPermission): string | null {
  if (to.meta?.multiAdmin) {
    return permission.isAdminOfAny ? null : "User needs to be administrator of a company to access this page";
  }
  if (to.meta?.userAdmin && !permission.admin) {
    return "User needs to be company administrator to get access";
  }
  if (to.meta?.tokenAdmin && !(permission.isGlobalAdmin || permission.user?.isToken)) {
    return "User needs to be global administrator";
  }
  return null;
}

// Router guard for login
router.beforeEach((to, _from, next) => {
  const permission: PartialPermission = store.getters.userPermission;
  util.checkRouteDebug(to);
  if (!to.meta?.userLogged) {
    next();
    return;
  }
  if (!permission.isLogged) {
    store.commit("setRedirect", to);
    next("/authlogin");
    return;
  }
  const deniedReason = checkRoutePermissions(to, permission) ?? checkAdminMeta(to, permission);
  if (deniedReason) {
    util.logError(deniedReason);
    next("/error?code=forbidden");
    return;
  }
  next();
});

if (config.analytics.enabled) {
  gtag(router, config.analytics.trackingID, config.analytics.opts);
}

export default router;
